import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLazyGetQuizByIdQuery } from '../../slices/fun_stuff/quizSlice';
import QuizHeader from "../../components/fun_stuff/QuizHeader";
import GenerateQuiz from "../../components/fun_stuff/GenerateQuiz";
import QuizList from "../../components/fun_stuff/QuizList";
import GradeQuizBtn from "../../components/fun_stuff/GradeQuizBtn";
import StartQuiz from "../../components/fun_stuff/StartQuiz";
import CountdownTimer from "../../components/fun_stuff/CountdownTimer";
import Question from "../../components/fun_stuff/Question";

const SECONDS_PER_QUESTION = 40;

const levels = {
    'elementary': 'Elementary',
    'high_school': 'High School',
    'honors': 'Honors',
    'university': 'University',
};

const QuizGameScreen = () => {
    const location = useLocation();

    const [quiz, setQuiz] = useState(null);
    const [newQuiz, setNewQuiz] = useState(true);

    const [gradeQuiz, setGradeQuiz] = useState(false);
    const [correctAnswers, setCorrectAnswers] = useState({});
    const [givenAnswers, setGivenAnswers] = useState({});
    const [quizStarted, setQuizStarted] = useState(false);
    const [showStart, setShowStart] = useState(false);
    const [quizTime, setQuizTime] = useState(600000);
    const [startTimer, setStartTimer] = useState(false);
    const [timeExpired, setTimeExpired] = useState(false);

    const [challengedQuiz, setChallengedQuiz] = useState(false);
    const [liveQuiz, setLiveQuiz] = useState(false);

    const [showAnswers, setShowAnswers] = useState(false);
    const [showCheck, setShowCheck] = useState({});
    const [showX, setShowX] = useState({});

    const [currentQuiz, setCurrentQuiz] = useState({
        id: 0,
        topic: '',
        level: '',
        number_questions: 1,
        number_correct: 0,
    });

    // For beta testers: 
    const [quizzesRemaining, setQuizzesRemaing] = useState(10);


    const [fetchQuizById, { data: fetchedQuiz, isSuccess }] = useLazyGetQuizByIdQuery();

    useEffect(() => {
        if (location.state) {
            const { gradeQuiz, linkedQuizId, liveQuiz } = location.state;
            setGradeQuiz(gradeQuiz || false);
            setChallengedQuiz(true);
            setNewQuiz(true);
    
            if (linkedQuizId) {
                setQuiz(null);
                fetchQuizById(linkedQuizId);
            } else if (liveQuiz) {
                setLiveQuiz(true);
                setQuiz(liveQuiz);
            }
        }
    }, [location.state, fetchQuizById]);

    useEffect(() => {
        if (isSuccess && fetchedQuiz) {
            setQuiz(fetchedQuiz);
        }
    }, [isSuccess, fetchedQuiz]);

    useEffect(() => {
        if (newQuiz && quiz) { 
            setNewQuiz(false);
            setShowStart(true);
            setCurrentQuiz({
                id: quiz.id,
                topic: quiz.topic,
                level: quiz.level,
                number_questions: quiz.questions.length,
                number_correct: 0,
            });
            let tempAnswers = {}
            for (let i = 0; i < quiz.questions.length; i++) {
                tempAnswers[i] = false;
            }
            setCorrectAnswers(tempAnswers);
        }
    }, [quiz, newQuiz]);

    useEffect(() => {
        if (gradeQuiz && quizStarted) {
            setStartTimer(true);
        }
    }, [quizStarted, gradeQuiz]);

    useEffect(() => {
        if (gradeQuiz && quiz?.questions?.length) {
            setStartTimer(false);  
            setQuizTime(quiz.questions.length * SECONDS_PER_QUESTION * 1000);
        }
    }, [gradeQuiz, quiz]);
    
    function hideAnswers() {
        setNewQuiz(true);
        setShowAnswers(false);
        setShowCheck({});
        setShowX({});
        setGradeQuiz(false);
        setChallengedQuiz(false);
        setLiveQuiz(false);
    }

    function resetQuiz() {
        setQuiz(null);
        hideAnswers();
        setQuizStarted(false);
    }

    function checkAnswer(index, option) {
        if (option.toLowerCase() === quiz.questions[index].correct_answer.toLowerCase()) {
            setCorrectAnswers({ ...correctAnswers, [index]: true });
            if (!gradeQuiz && !liveQuiz) {
                toggleCheck(index);
            }
        } else {
            setCorrectAnswers({ ...correctAnswers, [index]: false });
            if (!gradeQuiz && !liveQuiz) {
                toggleX(index);
            }
        }
        setGivenAnswers({ ...givenAnswers, [index]: option});
    }

    function toggleCheck(index) {
        setShowCheck({ ...showCheck, [index]: true });
        setShowX({ ...showX, [index]: false });
    }   

    function toggleX(index) {
        setShowX({ ...showX, [index]: true });
        setShowCheck({ ...showCheck, [index]: false });
    }

    return (
    <>
    <div id="quiz-game">
        {!quizStarted &&
        <>
        <QuizHeader profile={false} />

        <GenerateQuiz 
            setQuiz={setQuiz}
            hideAnswers={hideAnswers}
            setQuizzesRemaing={setQuizzesRemaing}
            quizzesRemaining={quizzesRemaining} 
        />

        <QuizList 
            onQuizSelect={setQuiz} 
            hideAnswers={hideAnswers}
        />
        </>
        }

        {showStart && 
        <StartQuiz 
            gradeQuiz={gradeQuiz}
            setGradeQuiz={setGradeQuiz}
            setQuizStarted={setQuizStarted}
            setShowStart={setShowStart}
            challenged={challengedQuiz}
            setQuiz={setQuiz}
            liveQuiz={liveQuiz}
        />}

        {(gradeQuiz || liveQuiz) && !timeExpired && 
        <CountdownTimer 
            key={quizTime}
            timeMs={quizTime}
            startTimer={startTimer}
            onExpire={setTimeExpired}
        />}

        {quiz && 
        <div id="output-text">
            <h2 id="title">{`${quiz.topic} - ${levels[quiz.level]}`}</h2>
            {quiz?.questions.map((question, index) => (
            <Question 
                key={`${question.question}-${index}`}
                question={question} 
                index={index} 
                checkAnswer={checkAnswer}
                showX={showX[index]}
                showCheck={showCheck[index]}
                showAnswers={showAnswers}
                active={true} />
            ))}
        </div>}
             
        <div className="d-flex justify-content-center my-4">
            {quiz &&
            <GradeQuizBtn 
                currentQuiz={currentQuiz} 
                correctAnswers={correctAnswers}
                givenAnswers={givenAnswers}
                quizStarted={quizStarted}
                setQuizStarted={setQuizStarted}
                setShowAnswers={setShowAnswers}
                setShowCheck={setShowCheck}
                setShowX={setShowX}
                challenged={challengedQuiz}
                timeExpired={timeExpired}
                setStartTimer={setStartTimer}
                graded={gradeQuiz}
                resetQuiz={resetQuiz}
                showSubmit={!showAnswers}
                liveQuiz={liveQuiz}
            />}
        </div>
    </div>
    </>
  )
}

export default QuizGameScreen
